import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import { ProbationHeader } from "./ProbationHeader";
import { matchCandidateRecord } from "./probationSearchUtils";
import { tokenService } from "../../../core/utils/tokenService";
import DOMPurify from "dompurify";

type ConfirmedEmployee = {
  srNo?: number;
  cycleId?: number | string;
  probationCycleId?: number | string;
  empId: string;
  empName?: string;
  designation?: string;
  department?: string;
  category?: string;
  joiningDate?: string;
  confirmationDate?: string;
  officeOrderNo: string;
  officeOrderDate?: string;
  status: string;
  [key: string]: any;
};

const ProbationOfficeOrders: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramCycleId = queryParams.get("cycleId");

  const [selectedCycleId, setSelectedCycleId] = useState<number | string>("");
  const [confirmedEmployees, setConfirmedEmployees] = useState<ConfirmedEmployee[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadingSingleEmpId, setDownloadingSingleEmpId] = useState<string | null>(null);
  const [viewingSingleEmpId, setViewingSingleEmpId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  const tableRef = useRef<any>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  const getTodayDateStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatToIsoDate = (val: string) => {
    if (!val || val === "N/A") return getTodayDateStr();
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    const dmyMatch = val.match(/^(\d{1,2})[.\-\/](\d{1,2})[.\-\/](\d{4})/);
    if (dmyMatch) {
      const day = dmyMatch[1].padStart(2, "0");
      const month = dmyMatch[2].padStart(2, "0");
      const year = dmyMatch[3];
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return getTodayDateStr();
  };

  const [orderNumbers, setOrderNumbers] = useState<Record<string, string>>({});
  const [orderDates, setOrderDates] = useState<Record<string, string>>({});
  const [bulkOrderNo, setBulkOrderNo] = useState<string>("");
  const [bulkOrderDate, setBulkOrderDate] = useState<string>(getTodayDateStr());

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  // Reset pagination on filter or cycle changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [selectedCycleId, searchTerm]);

  const handleCycleChange = (id: string | number) => {
    setSelectedCycleId(id);
  };

  const fetchConfirmedEmployees = async () => {
    if (!selectedCycleId) return;
    setLoading(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.OFFICE_ORDER_DATA,
        { cycleId: Number(selectedCycleId) },
        true
      );
      const resData = response?.data || response;
      const innerData = resData?.data || resData;

      let list: any[] = [];
      if (Array.isArray(innerData)) {
        list = innerData;
      } else if (Array.isArray(innerData?.officeOrderData)) {
        list = innerData.officeOrderData;
      } else if (Array.isArray(innerData?.employees)) {
        list = innerData.employees;
      } else if (Array.isArray(innerData?.candidates)) {
        list = innerData.candidates;
      } else if (Array.isArray(innerData?.items)) {
        list = innerData.items;
      } else if (typeof innerData === "object" && innerData !== null) {
        const arrays = Object.values(innerData).filter(val => Array.isArray(val)) as any[];
        list = arrays.flat();
      }

      const today = getTodayDateStr();

      const sanitizeOfficeOrderNo = (val: any) => {
        if (!val || typeof val !== "string") return "";
        // If string contains Devanagari (Hindi) characters, treat as blank
        if (/[\u0900-\u097F]/.test(val)) return "";
        return val.trim();
      };

      // The office-order-data endpoint is the source of truth for this table.
      // Leave officeOrderNo completely blank if no valid English/code order number is present
      const approvedList = list.map((emp: any) => {
        const rawOrderNo = emp.officeOrderNo || emp.office_order_no || "";
        const cleanOrderNo = sanitizeOfficeOrderNo(rawOrderNo);

        return {
          srNo: emp.s_no,
          cycleId: emp.cycleId || emp.cycle_id,
          probationCycleId: emp.probation_cycle_id,
          empId: emp.empId || emp.emp_no,
          empName: emp.empName || emp.emp_name,
          designation: emp.designation || emp.designationId || "N/A",
          category: emp.category || "N/A",
          joiningDate: emp.joiningDate || emp.joining_date || "N/A",
          confirmationDate: emp.confirmationDate || emp.confirmation_date || "N/A",
          officeOrderNo: cleanOrderNo,
          officeOrderDate: formatToIsoDate(emp.officeOrderDate || emp.office_order_date),
          status: emp.approvalStatus || emp.status || "APPROVED"
        };
      });

      setConfirmedEmployees(approvedList);

      const initialNumbers: Record<string, string> = {};
      const initialDates: Record<string, string> = {};
      const allKeys: string[] = [];
      approvedList.forEach((emp: any) => {
        const empKey = String(emp.empId);
        allKeys.push(empKey);
        initialNumbers[emp.empId] = emp.officeOrderNo || "";
        initialDates[emp.empId] = formatToIsoDate(emp.officeOrderDate);
      });
      setOrderNumbers(initialNumbers);
      setOrderDates(initialDates);
      setSelectedRowKeys(allKeys);
      setTimeout(() => {
        tableRef.current?.resetSelection(allKeys);
      }, 0);
    } catch (e) {
      console.error("Failed to load confirmed employees", e);
      setConfirmedEmployees([]);
      setSelectedRowKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmedEmployees();
  }, [selectedCycleId]);

  const handleApplySelected = () => {
    if (selectedRowKeys.length === 0) {
      toast.warning("Please select at least one employee from the list.");
      return;
    }
    if (!bulkOrderNo.trim() && !bulkOrderDate.trim()) {
      toast.warning("Please enter an order number or date to apply.");
      return;
    }
    const updatedNumbers = { ...orderNumbers };
    const updatedDates = { ...orderDates };
    selectedRowKeys.forEach(key => {
      const empId = String(key);
      if (bulkOrderNo.trim()) {
        const resolvedNo = bulkOrderNo.includes("{empId}") 
          ? bulkOrderNo.replace("{empId}", empId)
          : bulkOrderNo;
        updatedNumbers[empId] = resolvedNo;
      }
      if (bulkOrderDate.trim()) {
        updatedDates[empId] = bulkOrderDate;
      }
    });
    setOrderNumbers(updatedNumbers);
    setOrderDates(updatedDates);
    const count = selectedRowKeys.length;

    // Clear selection after applying
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);

    toast.success(`Applied bulk details to ${count} employee(s) and cleared selection.`);
  };

  const handleApplyAll = () => {
    if (confirmedEmployees.length === 0) {
      toast.warning("No employees available to apply.");
      return;
    }
    if (!bulkOrderNo.trim() && !bulkOrderDate.trim()) {
      toast.warning("Please enter an order number or date to apply.");
      return;
    }
    const updatedNumbers = { ...orderNumbers };
    const updatedDates = { ...orderDates };
    confirmedEmployees.forEach(emp => {
      const empId = String(emp.empId);
      if (bulkOrderNo.trim()) {
        const resolvedNo = bulkOrderNo.includes("{empId}") 
          ? bulkOrderNo.replace("{empId}", empId)
          : bulkOrderNo;
        updatedNumbers[empId] = resolvedNo;
      }
      if (bulkOrderDate.trim()) {
        updatedDates[empId] = bulkOrderDate;
      }
    });
    setOrderNumbers(updatedNumbers);
    setOrderDates(updatedDates);

    // Clear selection after applying
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);

    toast.success(`Applied bulk office order details to all ${confirmedEmployees.length} employee(s).`);
  };

  const handleAutoFormat = () => {
    const today = getTodayDateStr();
    const year = new Date().getFullYear();
    const targetEmployees = selectedRowKeys.length > 0
      ? confirmedEmployees.filter(emp => selectedRowKeys.includes(String(emp.empId)))
      : confirmedEmployees;

    if (targetEmployees.length === 0) {
      toast.warning("No employees available to format.");
      return;
    }

    const updatedNumbers = { ...orderNumbers };
    const updatedDates = { ...orderDates };
    targetEmployees.forEach(emp => {
      const empId = String(emp.empId);
      updatedNumbers[empId] = `DMRC/HR/CONF/${year}-${year + 1}/Q1/${empId}`;
      updatedDates[empId] = today;
    });
    setOrderNumbers(updatedNumbers);
    setOrderDates(updatedDates);

    // Clear selection after auto formatting
    setSelectedRowKeys([]);
    tableRef.current?.resetSelection([]);

    toast.success(`Auto-generated standard order numbers for ${targetEmployees.length} employee(s).`);
  };

  const handleClear = () => {
    if (selectedRowKeys.length > 0 && selectedRowKeys.length < confirmedEmployees.length) {
      const updatedNumbers = { ...orderNumbers };
      selectedRowKeys.forEach(key => {
        const empId = String(key);
        updatedNumbers[empId] = "";
      });
      setOrderNumbers(updatedNumbers);
      const count = selectedRowKeys.length;
      setSelectedRowKeys([]);
      tableRef.current?.resetSelection([]);
      toast.info(`Cleared office order number for ${count} selected employee(s).`);
    } else {
      setOrderNumbers({});
      setBulkOrderNo("");
      setSelectedRowKeys([]);
      tableRef.current?.resetSelection([]);
      toast.info("Cleared all custom office order numbers.");
    }
  };

  const handleGenerateOfficeOrder = async () => {
    if (!selectedCycleId) {
      toast.error("Please select a probation cycle first.");
      return;
    }
    if (confirmedEmployees.length === 0) {
      toast.warning("No confirmed employees available.");
      return;
    }
    setGenerating(true);
    try {
      const today = getTodayDateStr();
      const year = new Date().getFullYear();
      const candidates = confirmedEmployees.map(emp => ({
        empId: String(emp.empId),
        officeOrderNo: (orderNumbers[emp.empId] !== undefined && orderNumbers[emp.empId].trim()) 
          ? orderNumbers[emp.empId].trim() 
          : (emp.officeOrderNo || `DMRC/HR/CONF/${year}-${year + 1}/Q1/${emp.empId}`),
        officeOrderDate: (orderDates[emp.empId] !== undefined && orderDates[emp.empId].trim()) 
          ? orderDates[emp.empId].trim() 
          : (emp.officeOrderDate && emp.officeOrderDate !== "N/A" ? emp.officeOrderDate : today)
      }));

      const payload = {
        cycleId: Number(selectedCycleId),
        candidates
      };

      console.log("Submitting fill-office-order payload:", payload);

      await Api.post(API_URLS.PROBATION.FILL_OFFICE_ORDER, payload, true);

      toast.success("Office confirmation orders generated and saved successfully!");
      setShowModal(true);
      fetchConfirmedEmployees();
    } catch (e: any) {
      console.error("Generate order failed", e);
      toast.error("Failed to generate order: " + Api.getErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedCycleId) {
      toast.error("Please select a probation cycle first.");
      return;
    }
    setDownloading(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.OFFICE_ORDER_HTML,
        { cycleId: Number(selectedCycleId) },
        true
      );
      const responseData = response?.data ?? response;
      const html = typeof responseData === "string"
        ? responseData
        : responseData?.html || responseData?.data?.html || responseData?.data;

      if (typeof html !== "string" || !html.trim()) {
        throw new Error("Office order HTML was not returned by the server.");
      }

      const sanitizedHtml = DOMPurify.sanitize(html, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ["style", "link", "meta", "title"],
        ADD_ATTR: ["target", "rel", "href", "class", "style"]
      });
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        throw new Error("Please allow pop-ups to download the office order PDF.");
      }
      printWindow.document.write(sanitizedHtml);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
      toast.success("Office order PDF print window opened successfully!");
    } catch (error: any) {
      console.error("Failed to download PDF", error);
      toast.error("Failed to download office order PDF: " + error.message);
    } finally {
      setDownloading(false);
    }
  };

  const processPdfResponse = async (response: Response, empId: string): Promise<Blob | null> => {
    const contentType = (response.headers.get("content-type") || "").toLowerCase();
    
    // 1. If response is JSON
    if (contentType.includes("application/json")) {
      const json = await response.json();
      console.log("PDF endpoint returned JSON:", json);
      
      // Check if it contains base64 PDF
      const base64Data = json?.data?.pdf || json?.data?.base64 || json?.pdf || json?.base64 || (typeof json?.data === "string" && json.data.startsWith("JVBERi") ? json.data : null);
      if (base64Data) {
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: "application/pdf" });
      }

      // Check if it returned a direct URL to PDF
      const pdfUrl = json?.data?.url || json?.data?.pdfUrl || json?.url || json?.pdfUrl || json?.fileUrl;
      if (pdfUrl) {
        window.open(pdfUrl, "_blank");
        return null;
      }

      // Check if it returned HTML instead
      const htmlContent = json?.data?.html || json?.html;
      if (htmlContent) {
        const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
          WHOLE_DOCUMENT: true,
          ADD_TAGS: ["style", "link", "meta", "title"],
          ADD_ATTR: ["target", "rel", "href", "class", "style"]
        });
        const win = window.open("", "_blank");
        if (win) {
          win.document.write(sanitizedHtml);
          win.document.close();
          win.focus();
          setTimeout(() => {
            win.print();
          }, 500);
        }
        return null;
      }

      // Otherwise it's an error/message in JSON
      const errMsg = json?.message || json?.error || json?.msg || JSON.stringify(json);
      throw new Error(errMsg);
    }

    // 2. If response is HTML
    if (contentType.includes("text/html")) {
      const htmlText = await response.text();
      console.log("PDF endpoint returned HTML instead of PDF binary:", htmlText.substring(0, 300));
      
      if (htmlText.includes("<html") || htmlText.includes("<body") || htmlText.includes("<div") || htmlText.includes("<table")) {
        const sanitizedHtml = DOMPurify.sanitize(htmlText, {
          WHOLE_DOCUMENT: true,
          ADD_TAGS: ["style", "link", "meta", "title"],
          ADD_ATTR: ["target", "rel", "href", "class", "style"]
        });
        const win = window.open("", "_blank");
        if (win) {
          win.document.write(sanitizedHtml);
          win.document.close();
          win.focus();
          setTimeout(() => {
            win.print();
          }, 500);
        }
        return null;
      }
      throw new Error("Server returned HTML text instead of PDF document: " + htmlText.substring(0, 100));
    }

    // 3. Binary response
    const arrayBuffer = await response.arrayBuffer();
    const uint8 = new Uint8Array(arrayBuffer);
    
    // Check for PDF magic bytes: '%PDF' (0x25, 0x50, 0x44, 0x46)
    if (uint8.length >= 4 && uint8[0] === 0x25 && uint8[1] === 0x50 && uint8[2] === 0x44 && uint8[3] === 0x46) {
      return new Blob([arrayBuffer], { type: "application/pdf" });
    }

    // If it doesn't start with %PDF, decode text to see what was returned
    const textDecoder = new TextDecoder();
    const textContent = textDecoder.decode(uint8.slice(0, 500));
    console.warn("Downloaded file is not a valid PDF. Content starts with:", textContent);
    
    try {
      const parsedJson = JSON.parse(textContent);
      throw new Error(parsedJson.message || parsedJson.error || "Server returned non-PDF data.");
    } catch (e: any) {
      if (e.message && !e.message.includes("Unexpected")) {
        throw e;
      }
      throw new Error("Received non-PDF response from server: " + textContent.substring(0, 120));
    }
  };

  const handleDownloadSinglePdf = async (empId: string) => {
    if (!selectedCycleId || !empId) return;
    setDownloadingSingleEmpId(empId);
    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "application/pdf, application/json, text/html"
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URLS.PROBATION.GENERATE_OFFICE_ORDER_PDF}?cycleId=${selectedCycleId}&empId=${empId}`, {
        method: "GET",
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const pdfBlob = await processPdfResponse(response, empId);
      if (pdfBlob) {
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `office_order_${empId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        toast.success(`Office order PDF for ${empId} downloaded successfully!`);
      }
    } catch (error: any) {
      console.error("Failed to download PDF", error);
      toast.error("Failed to download office order PDF: " + error.message);
    } finally {
      setDownloadingSingleEmpId(null);
    }
  };

  const handleViewSinglePdf = async (empId: string) => {
    if (!selectedCycleId || !empId) return;
    setViewingSingleEmpId(empId);
    try {
      const token = tokenService.getToken();
      const headers: any = {
        "Accept": "application/pdf, application/json, text/html"
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URLS.PROBATION.GENERATE_OFFICE_ORDER_PDF}?cycleId=${selectedCycleId}&empId=${empId}`, {
        method: "GET",
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }

      const pdfBlob = await processPdfResponse(response, empId);
      if (pdfBlob) {
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const printWindow = window.open(blobUrl, "_blank");
        if (!printWindow) {
          throw new Error("Please allow pop-ups to view or print the office order PDF.");
        }
        toast.success(`Office order PDF for ${empId} opened!`);
      }
    } catch (error: any) {
      console.error("Failed to view PDF", error);
      toast.error("Failed to open office order PDF: " + error.message);
    } finally {
      setViewingSingleEmpId(null);
    }
  };

  const filteredEmployees = useMemo(() => {
    const list = confirmedEmployees.map((emp, idx) => ({
      ...emp,
      key: emp.empId ? String(emp.empId) : String(idx),
      id: emp.empId || String(idx),
    }));

    return list.filter((emp) => matchCandidateRecord(emp, searchTerm));
  }, [confirmedEmployees, searchTerm, orderNumbers]);

  // Define Table Columns
  const columns = useMemo(() => {
    return [
      {
        title: "Sr. No.",
        dataIndex: "srNo",
        render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
        width: 70
      },
      {
        title: "Emp No",
        dataIndex: "empId",
        render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
      },
      {
        title: "Employee Name",
        dataIndex: "empName",
        render: (val: string) => <span className="fw-bold text-dark">{val}</span>
      },
      {
        title: "Category",
        dataIndex: "category",
        render: (val: string) => <span className="text-dark small fw-medium">{val || "N/A"}</span>
      },
      {
        title: "Joining Date",
        dataIndex: "joiningDate"
      },
      {
        title: "Confirmation Date",
        dataIndex: "confirmationDate"
      },
      {
        title: "Office Order Number",
        dataIndex: "officeOrderNo",
        width: 280,
        render: (_: any, record: any) => {
          const currentVal = orderNumbers[record.empId] !== undefined ? orderNumbers[record.empId] : (record.officeOrderNo || "");
          return (
            <input
              type="text"
              className="form-control form-control-sm text-primary fw-medium bg-white"
              value={currentVal}
              placeholder=""
              onChange={(e) => {
                const val = e.target.value;
                setOrderNumbers(prev => ({
                  ...prev,
                  [record.empId]: val
                }));
              }}
              style={{ minWidth: "220px", fontSize: "12px", height: "32px", borderColor: "#cbd5e1" }}
            />
          );
        }
      },
      {
        title: "Office Order Date",
        dataIndex: "officeOrderDate",
        width: 170,
        render: (_: any, record: any) => {
          const currentVal = orderDates[record.empId] !== undefined 
            ? formatToIsoDate(orderDates[record.empId]) 
            : formatToIsoDate(record.officeOrderDate);
          return (
            <input
              type="date"
              className="form-control form-control-sm text-dark bg-white"
              value={currentVal}
              onChange={(e) => {
                const val = e.target.value;
                setOrderDates(prev => ({
                  ...prev,
                  [record.empId]: val
                }));
              }}
              style={{ minWidth: "135px", fontSize: "12px", height: "32px", borderColor: "#cbd5e1" }}
            />
          );
        }
      },
      {
        title: "Office Order Date",
        dataIndex: "officeOrderDate"
      },
      {
        title: "Status",
        dataIndex: "status",
        align: "center",
        render: (val: string) => <span className="badge bg-success-light text-success">{val || "APPROVED"}</span>
      },
      {
        title: "Actions",
        dataIndex: "actions",
        align: "right",
        render: (_: any, record: any) => (
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-icon btn-sm btn-outline-primary"
              title="Download PDF"
              onClick={() => handleDownloadSinglePdf(record.empId)}
              disabled={downloadingSingleEmpId === record.empId}
            >
              {downloadingSingleEmpId === record.empId ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <i className="ti ti-download"></i>
              )}
            </button>
            <button
              className="btn btn-icon btn-sm btn-outline-info"
              title="View / Print PDF"
              onClick={() => handleViewSinglePdf(record.empId)}
              disabled={viewingSingleEmpId === record.empId}
            >
              {viewingSingleEmpId === record.empId ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <i className="ti ti-printer"></i>
              )}
            </button>
          </div>
        )
      }
    ];
  }, [pagination, downloadingSingleEmpId, viewingSingleEmpId, orderNumbers, orderDates]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <ProbationHeader
          title="Confirmation Office Orders"
          subtitle="Generate and print service confirmation orders for cleared employees"
          selectedCycleId={selectedCycleId}
          onCycleChange={handleCycleChange}
          extra={
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button
                className="btn btn-warning text-white btn-sm d-inline-flex align-items-center"
                onClick={handleGenerateOfficeOrder}
                disabled={generating || confirmedEmployees.length === 0}
                style={{ height: 36 }}
              >
                {generating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="ti ti-file-text me-1"></i> Generate Office Order
                  </>
                )}
              </button>
              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={handleDownloadPdf}
                disabled={downloading || confirmedEmployees.length === 0}
                style={{ height: 36 }}
              >
                {downloading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-download me-1"></i> Download PDF
                  </>
                )}
              </button>
            </div>
          }
        />

        <div className="card">
          <div className="card-body">
            {/* BULK OFFICE ORDER ENTRY TOOLBAR */}
            <div className="p-3 mb-3 bg-light rounded-3 shadow-xs border">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                  <span className="fw-semibold text-dark small text-nowrap">
                    <i className="ti ti-edit me-1 text-primary"></i> Bulk Entry:
                  </span>
                  <div style={{ minWidth: "220px", flex: "1 1 240px", maxWidth: "340px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm bg-white"
                      placeholder=""
                      value={bulkOrderNo}
                      onChange={(e) => setBulkOrderNo(e.target.value)}
                      style={{ fontSize: "12px", height: "32px" }}
                    />
                  </div>
                  <div style={{ width: "140px" }}>
                    <input
                      type="date"
                      className="form-control form-control-sm bg-white"
                      value={bulkOrderDate}
                      onChange={(e) => setBulkOrderDate(e.target.value)}
                      style={{ fontSize: "12px", height: "32px" }}
                    />
                  </div>
                  <button
                    className="btn btn-primary btn-sm px-2.5"
                    onClick={handleApplySelected}
                    disabled={selectedRowKeys.length === 0}
                    style={{ fontSize: "12px", height: "32px", whiteSpace: "nowrap" }}
                    title="Apply to selected employees"
                  >
                    <i className="ti ti-check me-1"></i> Apply Selected ({selectedRowKeys.length})
                  </button>
                  <button
                    className="btn btn-outline-primary btn-sm px-2.5"
                    onClick={handleApplyAll}
                    disabled={confirmedEmployees.length === 0}
                    style={{ fontSize: "12px", height: "32px", whiteSpace: "nowrap" }}
                    title="Apply to all employees"
                  >
                    Apply All ({confirmedEmployees.length})
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm px-2.5"
                    onClick={handleAutoFormat}
                    disabled={confirmedEmployees.length === 0}
                    style={{ fontSize: "12px", height: "32px", whiteSpace: "nowrap" }}
                    title="Auto-generate standard DMRC office order format"
                  >
                    <i className="ti ti-wand me-1"></i> Auto Format {selectedRowKeys.length > 0 && selectedRowKeys.length < confirmedEmployees.length ? `(${selectedRowKeys.length})` : ""}
                  </button>
                  <button
                    className="btn btn-outline-danger btn-sm px-2.5"
                    onClick={handleClear}
                    disabled={confirmedEmployees.length === 0}
                    style={{ fontSize: "12px", height: "32px", whiteSpace: "nowrap" }}
                  >
                    <i className="ti ti-trash me-1"></i> Clear
                  </button>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between mb-3">
              <input
                type="text"
                className="form-control w-50"
                placeholder="Search name, emp no or order number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ minWidth: 260, maxWidth: 520 }}
              />
              <div className="text-muted small">
                Selected: <span className="fw-bold text-dark">{selectedRowKeys.length}</span> of {confirmedEmployees.length}
              </div>
            </div>

            <Table
              ref={tableRef}
              columns={columns}
              dataSource={filteredEmployees}
              paginationConfig={pagination}
              Selection={true}
              onRowSelection={(keys) => setSelectedRowKeys(keys)}
              loading={loading}
              scroll={{ x: "max-content" }}
              handlePagination={(pag: any) => {
                setPagination({
                  current: pag.current,
                  pageSize: pag.pageSize
                });
              }}
            />
          </div>
        </div>
      </div>

      {/* Office Order Generated Success Modal */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} role="dialog">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px" }}>
              <div className="modal-header bg-success text-white border-0 py-3" style={{ borderTopLeftRadius: "12px", borderTopRightRadius: "12px" }}>
                <h5 className="modal-title fw-bold text-white d-flex align-items-center gap-2">
                  <i className="ti ti-circle-check fs-4"></i>
                  Office Orders Generated
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4 text-center">
                <i className="ti ti-circle-check text-success mb-3 d-block" style={{ fontSize: "64px" }}></i>
                <h5 className="fw-bold text-dark">Orders Generated Successfully!</h5>
                <p className="text-muted mb-0">
                  Confirmation office orders have been generated and dispatched to the employee records. The corporate SAP ledger will be updated accordingly.
                </p>
              </div>
              <div className="modal-footer border-0 pt-0 pb-4 justify-content-center">
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={() => setShowModal(false)}
                  style={{ borderRadius: "6px" }}
                >
                  OK, Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationOfficeOrders;
